import type { CSSProperties, ReactNode } from 'react';
import { color, font } from './tokens';

import type { ShellVariant } from './navStyle';

export type { ShellVariant };

export interface ShellProps {
  variant: ShellVariant;
  /** Logo mark and product name block at the top of the sidebar. */
  brand: ReactNode;
  /** Navigation links; style each with navItemStyle. */
  nav: ReactNode;
  /** Signed-in user block pinned to the bottom of the sidebar. */
  footer?: ReactNode;
  children: ReactNode;
}

const sidebar: Record<ShellVariant, CSSProperties> = {
  console: { background: color.consoleSidebar },
  portal: { background: color.surface, borderRight: `1px solid ${color.border}` },
};

/**
 * Sidebar + main layout from the designs. The sidebar wraps above the main
 * column on narrow screens (flex-wrap), as the mockups do. Side by side, the
 * sidebar is pinned to the viewport and scrolls on its own
 * (`.central-shell-sidebar` in styles.css), so the footer stays in view.
 */
export function Shell({ variant, brand, nav, footer, children }: ShellProps) {
  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexWrap: 'wrap',
        background: color.page,
        fontFamily: font.sans,
        color: color.text,
      }}
    >
      <aside
        className="central-shell-sidebar"
        style={{
          flex: '1 1 232px',
          maxWidth: '100%',
          padding: '20px 12px',
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          gap: 28,
          ...sidebar[variant],
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '0 8px' }}>
          {brand}
        </div>
        <nav aria-label={variant === 'console' ? 'Admin' : 'Portal'}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>{nav}</div>
        </nav>
        {footer && (
          <div
            style={{
              marginTop: 'auto',
              padding: 12,
              fontSize: 13,
              borderTop: `1px solid ${variant === 'console' ? color.consoleNavActiveBg : color.border}`,
              color: variant === 'console' ? color.consoleFooterText : color.textMuted,
            }}
          >
            {footer}
          </div>
        )}
      </aside>
      <main
        style={{
          flex: '999 1 560px',
          minWidth: 0,
          padding: '28px 32px',
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          gap: 24,
        }}
      >
        {children}
      </main>
    </div>
  );
}

export interface PageHeaderProps {
  title: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
}

/** H1 with a muted line under it and actions on the right. */
export function PageHeader({ title, subtitle, actions }: PageHeaderProps) {
  return (
    <header
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 16,
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
    >
      <div>
        <h1 style={{ margin: 0, fontSize: 26, lineHeight: '34px' }}>{title}</h1>
        {subtitle && <div style={{ fontSize: 14, color: color.textMuted }}>{subtitle}</div>}
      </div>
      {actions && <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>{actions}</div>}
    </header>
  );
}
